import { contentCacheService } from './pdfCacheService';
import { apiCache } from '../utils/offlineStorage';

/**
 * Offline Manager
 * Coordinates offline functionality across all services
 */
export class OfflineManager {
  constructor() {
    this.isOnline = navigator.onLine;
    this.listeners = new Set();
    
    // Listen for online/offline events
    window.addEventListener('online', this.handleOnline.bind(this));
    window.addEventListener('offline', this.handleOffline.bind(this));
  }

  /**
   * Add listener for online/offline status changes
   * @param {Function} callback - Callback function
   */
  addListener(callback) {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  /**
   * Handle online event
   */
  handleOnline() {
    this.isOnline = true;
    this.notifyListeners('online');
    console.log('App is now online - syncing cached data...');
    this.syncOfflineData();
  }

  /**
   * Handle offline event
   */
  handleOffline() {
    this.isOnline = false;
    this.notifyListeners('offline');
    console.log('App is now offline - using cached data...');
  }

  /**
   * Notify all listeners of status change
   * @param {string} status - 'online' or 'offline'
   */
  notifyListeners(status) {
    this.listeners.forEach(callback => {
      try {
        callback(status, this.isOnline);
      } catch (error) {
        console.error('Error in offline status listener:', error);
      }
    });
  }

  /**
   * Sync offline data when coming back online
   */
  async syncOfflineData() {
    try {
      // This could include syncing any queued operations
      // For now, we'll just log that we're back online
      console.log('Syncing offline data...');
      
      // You could add logic here to:
      // - Retry failed API calls
      // - Upload any offline-created content
      // - Refresh cached data
      
    } catch (error) {
      console.error('Error syncing offline data:', error);
    }
  }

  /**
   * Get comprehensive cache statistics
   */
  async getCacheStatistics() {
    try {
      const [contentStats, apiStats] = await Promise.all([
        contentCacheService.getCacheStats(),
        apiCache.getStats()
      ]);

      return {
        content: contentStats,
        api: apiStats,
        totalSize: (contentStats.totalSize || 0) + (apiStats.estimatedSize || 0),
        isOnline: this.isOnline
      };
    } catch (error) {
      console.error('Error getting cache statistics:', error);
      return {
        content: { totalItems: 0, totalSize: 0, cachedContent: [] },
        api: { totalEntries: 0, estimatedSize: 0 },
        totalSize: 0,
        isOnline: this.isOnline
      };
    }
  }

  /**
   * Clear all cached data
   */
  async clearAllCache() {
    try {
      await Promise.all([
        contentCacheService.clearCache(),
        apiCache.clear()
      ]);
      console.log('All cache cleared successfully');
      return true;
    } catch (error) {
      console.error('Error clearing cache:', error);
      return false;
    }
  }

  /**
   * Check if specific content is available offline
   * @param {string} contentId - Content ID
   * @param {string} contentType - Content type
   */
  async isContentAvailableOffline(contentId, contentType) {
    try {
      return await contentCacheService.isContentCached(contentId, contentType);
    } catch (error) {
      console.error('Error checking offline availability:', error);
      return false;
    }
  }

  /**
   * Preload content for offline use
   * @param {Array} items - Array of items to preload
   * @param {string} contentType - Content type
   * @param {Function} extractContentInfo - Function to extract content info
   */
  async preloadContent(items, contentType, extractContentInfo) {
    try {
      if (!this.isOnline) {
        console.warn('Cannot preload content while offline');
        return false;
      }

      console.log(`Preloading ${items.length} ${contentType} items for offline use...`);
      
      for (const item of items) {
        const contentInfo = extractContentInfo(item);
        if (contentInfo && contentInfo.urls) {
          for (let i = 0; i < contentInfo.urls.length; i++) {
            const url = contentInfo.urls[i];
            const contentId = `${item.id}_${i}`;
            const contentName = `${contentInfo.name} - Part ${i + 1}`;
            
            const isCached = await contentCacheService.isContentCached(contentId, contentType);
            if (!isCached) {
              await contentCacheService.cacheContent(url, contentId, contentName, contentType);
            }
          }
        }
      }
      
      console.log(`Preloading complete for ${contentType}`);
      return true;
    } catch (error) {
      console.error(`Error preloading ${contentType} content:`, error);
      return false;
    }
  }

  /**
   * Get offline status
   */
  getStatus() {
    return {
      isOnline: this.isOnline,
      hasCache: true, // We always have caching capability
      lastSync: new Date().toISOString() // Could be stored and retrieved
    };
  }
}

// Create singleton instance
export const offlineManager = new OfflineManager();

export default offlineManager;